export type PlaybackSpeed = "slow" | "normal" | "fast";
export type CameraMode = "cinematic" | "isometric" | "top" | "follow" | "orbit";
export type PathMode = "trail" | "full" | "off";
export type ThemeTransition = "per-move" | "between-tours";
export type Soundscape = "water" | "minimal" | "wood" | "crystal" | "ambient";
export type Performer = "knight" | "koi" | "catfish" | "turtle" | "duck" | "duck-family";
export type LandingEffect = "water" | "minimal" | "off";
export type SceneMode = "gallery" | "aquarium";
export type BoardTheme = "new-cycle" | "classic-cycle" | "all-cycle" | "ivory-forest" | "original-color" | "walnut" | "espresso" | "navy" | "forest" | "slate" | "burgundy" | "indigo" | "parchment" | "marble";

export interface MoveTiming { anticipationMs: number; jumpMs: number; landingMs: number; }
export const MOVE_TIMINGS: Record<PlaybackSpeed, MoveTiming> = {
  slow: { anticipationMs: 350, jumpMs: 650, landingMs: 180 },
  normal: { anticipationMs: 220, jumpMs: 430, landingMs: 120 },
  fast: { anticipationMs: 100, jumpMs: 230, landingMs: 70 },
};

function reduced(): boolean {
  if (typeof window === "undefined") return false;
  return window.innerWidth < 720 || (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false);
}

export class Settings {
  playbackSpeed: PlaybackSpeed;
  cameraMode: CameraMode = "cinematic";
  pathMode: PathMode = "trail";
  showMoveNumbers = false;
  boardTheme: BoardTheme = "new-cycle";
  showStartCue = true;
  themeTransition: ThemeTransition = "per-move";
  soundEnabled = false;
  soundVolume = 0.32;
  soundscape: Soundscape = "water";
  performer: Performer = "knight";
  landingEffect: LandingEffect;
  sceneMode: SceneMode = "gallery";
  lotusDecor = false;
  smallFish = false;
  paused = false;
  overlayVisible = true;
  reducedMode: boolean;
  dprCap: number;
  shadowMapSize: number;
  particlesEnabled: boolean;
  private listeners = new Set<() => void>();

  constructor() {
    this.reducedMode = reduced();
    this.playbackSpeed = this.reducedMode ? "fast" : "slow";
    this.dprCap = this.reducedMode ? 1.25 : 1.75;
    this.shadowMapSize = this.reducedMode ? 512 : 1536;
    this.particlesEnabled = !this.reducedMode;
    this.landingEffect = this.reducedMode ? "minimal" : "water";
  }
  get timing(): MoveTiming { return MOVE_TIMINGS[this.playbackSpeed]; }
  onChange(listener: () => void): () => void { this.listeners.add(listener); return () => this.listeners.delete(listener); }
  private emit(): void { for (const listener of this.listeners) listener(); }
  setPlaybackSpeed(speed: PlaybackSpeed): void { this.playbackSpeed = speed; this.emit(); }
  setCameraMode(mode: CameraMode): void { this.cameraMode = mode; this.emit(); }
  setPathMode(mode: PathMode): void { this.pathMode = mode; this.emit(); }
  setBoardTheme(theme: BoardTheme): void { this.boardTheme = theme; this.emit(); }
  setThemeTransition(mode: ThemeTransition): void { this.themeTransition = mode; this.emit(); }
  toggleMoveNumbers(): void { this.showMoveNumbers = !this.showMoveNumbers; this.emit(); }
  toggleStartCue(): void { this.showStartCue = !this.showStartCue; this.emit(); }
  toggleSound(): void { this.soundEnabled = !this.soundEnabled; this.emit(); }
  setSoundVolume(volume: number): void { this.soundVolume = Math.max(0, Math.min(1, volume)); this.emit(); }
  setSoundscape(soundscape: Soundscape): void { this.soundscape = soundscape; this.emit(); }
  setPerformer(performer: Performer): void { this.performer = performer; if (performer !== "knight") this.landingEffect = this.reducedMode ? "minimal" : "water"; this.emit(); }
  setLandingEffect(effect: LandingEffect): void { this.landingEffect = effect; this.emit(); }
  setSceneMode(mode: SceneMode): void { this.sceneMode=mode; if(mode==="aquarium"){this.performer="koi";this.landingEffect=this.reducedMode?"minimal":"water";this.soundscape="water";} this.emit(); }
  toggleLotusDecor():void { this.lotusDecor=!this.lotusDecor; this.emit(); }
  toggleSmallFish():void { this.smallFish=!this.smallFish; this.emit(); }
  togglePaused(): void { this.paused = !this.paused; this.emit(); }
  toggleOverlay(): void { this.overlayVisible = !this.overlayVisible; this.emit(); }
}
